import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, Mail, Phone, Trash2 } from "lucide-react";

const captions = { email: "Email", phone: "Обратный звонок" };
const whenLabels = { any: "В любое время", evening: "Вечером", weekend: "В выходные" };

export default function ProfessionalMapInbox({ cardId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  useEffect(() => { setOpen(false); setItems([]); setError(""); }, [cardId]);
  async function load() {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/professional-map/contact-requests/mine", { params: cardId ? { card_id: cardId } : {} });
      setItems(data || []);
    } catch (_) {
      setError("Не удалось загрузить обращения. Попробуй ещё раз.");
    } finally { setLoading(false); }
  }
  async function change(id, action) {
    if (busy) return;
    setBusy(id);
    try {
      const path = "/professional-map/contact-requests/mine/" + encodeURIComponent(id);
      if (action === "read") {
        await api.patch(path + "/read");
        setItems(prev => prev.map(row => row.id === id ? { ...row, read_at: new Date().toISOString() } : row));
      } else {
        await api.delete(path);
        setItems(prev => prev.filter(row => row.id !== id));
      }
    } catch (_) { setError("Не удалось обновить обращение."); }
    finally { setBusy(""); }
  }
  const unread = items.filter(row => !row.read_at).length;
  return (
    <section className="rounded-[24px] border border-[#826751]/20 bg-white/80 p-4 sm:p-6"
      data-testid="pm-owner-inbox">
      <button type="button" className="flex min-h-[44px] w-full items-center justify-between gap-3 text-left"
        aria-expanded={open} onClick={() => { if (!open) load(); setOpen(!open); }}
        data-testid="pm-owner-inbox-toggle">
        <span className="font-serif text-xl">Обращения клиентов {open && !loading && unread > 0 ? "(" + unread + " новых)" : ""}</span>
        <ChevronDown className={"transition-transform " + (open ? "rotate-180" : "")} size={18}/>
      </button>
      {open && <div className="mt-3 border-t border-[#826751]/15 pt-4">
        <p className="mb-4 text-xs leading-5 text-[#806B5C]">
          Личные заявки видишь только ты. Они не публикуются в общих вопросах и не относятся к службе поддержки.
        </p>
        <button type="button" onClick={load} disabled={loading}
          className="mb-4 min-h-[40px] rounded-lg border border-[#826751]/25 px-3 text-sm disabled:opacity-50">
          {loading ? "Обновляем…" : "Обновить обращения"}
        </button>
        {error && <p role="alert" className="mb-3 text-sm text-red-700">{error}</p>}
        {!loading && !error && items.length === 0 && <p className="text-sm text-[#755F4E]">Обращений пока нет.</p>}
        <div className="space-y-3">
          {items.map(row => <article key={row.id} className="rounded-xl border border-[#826751]/20 bg-[#FFFCF8] p-4"
            data-testid="pm-owner-contact-item">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong className="text-sm">{row.kind === "quote" ? "Запрос предложения" : "Связаться"}</strong>
              <span className="text-xs text-[#755F4E]">{new Date(row.created_at).toLocaleDateString("ru-RU")}</span>
            </div>
            {row.service && <p className="mt-2 text-sm">Услуга: {row.service}</p>}
            <p className="mt-1 text-xs text-[#755F4E]">Профессиональная страница: {row.card_id}</p>
          <p className="mt-2 flex items-center gap-2 text-sm">
              {row.channel === "phone" ? <Phone size={16}/> : <Mail size={16}/>}
              {captions[row.channel]}: <span className="break-all font-medium">{row.contact}</span>
            </p>
            {row.call_when && <p className="mt-1 text-xs text-[#755F4E]">Позвонить: {whenLabels[row.call_when] || "по договорённости"}</p>}
            <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6">{row.message}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {!row.read_at && <button type="button" disabled={busy === row.id}
                className="min-h-[40px] rounded-lg border border-[#826751]/25 px-3 text-xs"
                onClick={() => change(row.id, "read")}>Отметить прочитанным</button>}
              <button type="button" disabled={busy === row.id}
                className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-[#826751]/25 px-3 text-xs"
                onClick={() => change(row.id, "delete")}>
                <Trash2 size={14} aria-hidden="true"/> Удалить обращение
              </button>
            </div>
          </article>)}
        </div>
      </div>}
    </section>
  );
}
